import React, { useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import MiniStockChart from '../shared/MiniStockChart';
import ChartIntervalControl from '../shared/ChartIntervalControl';
import usePositionCalculations from '../../hooks/usePositionCalculations';

const pnlFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function formatSignedUsd(value) {
  if (!Number.isFinite(value)) return '—';
  const abs = pnlFormatter.format(Math.abs(value));
  if (value > 0) return `+${abs}`;
  if (value < 0) return `-${abs}`;
  return abs;
}

/**
 * Compact chart for an open position.
 * Header owns symbol / qty / P&L / interval; MiniStockChart is chart-only.
 * When hideMeta is true (e.g. positions table cards), only the chart renders —
 * interval is controlled by the parent toolbar.
 */
const PositionMiniChart = ({
  symbol,
  position,
  height = 250,
  hideMeta = false,
  fillHeight = false,
  interval: intervalProp,
  unit: unitProp,
  showPositionLine = false,
  exitLines = null,
  chartType = 'candle',
}) => {
  const { colors } = useTheme();
  const { calculateUnrealizedPL } = usePositionCalculations();
  const [intervalLocal, setIntervalLocal] = useState(1);
  const [unitLocal, setUnitLocal] = useState('Daily');

  const controlled = intervalProp != null && unitProp != null;
  const interval = controlled ? intervalProp : intervalLocal;
  const unit = controlled ? unitProp : unitLocal;

  const qty = Math.abs(parseFloat(position?.Quantity) || 0);
  const pnl = calculateUnrealizedPL(position);
  const pnlColor = pnl > 0 ? colors.success : pnl < 0 ? colors.danger : colors.textMuted;

  return (
    <div
      data-testid={`position-mini-chart-${symbol}`}
      style={fillHeight ? { height: '100%', display: 'flex', flexDirection: 'column' } : undefined}
    >
      {(!hideMeta) && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 8,
            gap: 8,
          }}
        >
          <div style={{ minWidth: 0 }}>
            <span style={{ fontSize: 17, fontWeight: 600, color: colors.text }}>
              {symbol}
            </span>
            {position && (
              <span
                style={{
                  marginLeft: 8,
                  fontSize: 13,
                  fontWeight: 500,
                  color: colors.textMuted,
                }}
              >
                {qty} shares
              </span>
            )}
            {position && (
              <span
                style={{
                  marginLeft: 8,
                  fontSize: 13,
                  fontWeight: 600,
                  color: pnlColor,
                }}
              >
                P&L {formatSignedUsd(pnl)}
              </span>
            )}
          </div>

          <ChartIntervalControl
            interval={interval}
            unit={unit}
            setIntervalAndUnit={(nextInterval, nextUnit) => {
              if (!controlled) {
                setIntervalLocal(nextInterval);
                setUnitLocal(nextUnit);
              }
            }}
            showSessionToggle={false}
            drop="down"
            align="end"
          />
        </div>
      )}

      <div style={{
        borderRadius: 8,
        overflow: 'hidden',
        ...(fillHeight ? { flex: 1, minHeight: 0 } : null),
      }}>
        <MiniStockChart
          symbol={symbol}
          colors={colors}
          height={height}
          fillHeight={fillHeight}
          showEmptyState={false}
          hideHeader
          interval={interval}
          unit={unit}
          positionPrice={showPositionLine ? position?.AveragePrice : null}
          positionQuantity={showPositionLine ? qty : null}
          exitLines={exitLines}
          chartType={chartType}
        />
      </div>
    </div>
  );
};

export default PositionMiniChart;
